/**
 * File: src/Pages/GMS/ISR/Components/embedSnippet.ts
 *
 * Builds the copy-paste embed code for a Guide channel's Widgets panel
 * (ADO #14203 — Host Integration: Self-Service Embed Code). Kept apart from
 * the dialog so it can be unit tested without pulling in the API layer.
 *
 * Replaces the earlier per-widget/per-launch-mode snippet (four launch modes,
 * an opt-in save/share checkbox, an advanced-attributes accordion for
 * sessionId/resume/edit) with the single-surface generator design settled
 * 11–14 Sept (Anu, Jabir, Sabin, Vinod — see okf/isr/e2e.md § "Widget panel —
 * Embed code" for the full settlement trail):
 *   - Site code   — ONE script for the whole channel, listing every widget in
 *                    compatibleProducts.dcConfig. Platform-aware. Paired with
 *                    a static HTML block (the two shared containers).
 *   - Widget placements — a snippet GENERATOR: widget + trigger + starting
 *                    state in, one placement snippet out. Nothing here is
 *                    stored on the widget; placement is documentation, not a
 *                    restriction (Jabir, 10 Sept).
 *   - Input parameters — per-widget parameter block, generated from the
 *                    widget's model's declared Input Parameters.
 *
 * Runtime contract (isr-runtime-generic), confirmed against the Compatio
 * Embed Handbook shared for this feature:
 *   - The platform client config lives under `compatioConfig[platForm]` —
 *     the object key MUST match the `platForm` value (handbook § 3: "Pick
 *     exactly one platform key ... The key must match compatioConfig.platForm").
 *     `platForm` itself sits at the TOP level of compatioConfig, never inside
 *     the platform object. ADO #14214 (US-2) left this as an open item
 *     ("Jabir's sample writes compatioConfig.<platform> ... confirm the
 *     namespace with the runtime team") — resolved here per the handbook,
 *     which is unambiguous. The single-widget snippet this file used to emit
 *     always wrote `compatioConfig.custom` regardless of platform; that was a
 *     latent bug for any non-custom platform once one became selectable.
 *   - `default: "1"` in a dcConfig entry is a banner-participation filter, not
 *     a preference — ADO #14214 (US-2) leaves its owner as an open item.
 *     Every entry is emitted with `default: "1"` here, unchanged from the
 *     shipped behaviour, pending that confirmation.
 *   - `dcConfig[].modelId` / `.widgetId` — exactly one is emitted, picked by
 *     identifierMode; widgetId always wins when the runtime sees both, so
 *     this never emits both at once.
 *   - `compatibleProducts.newUI` is unconditionally `true` here (the self-serve
 *     generator targets SB 3.x only — no per-model uiModelId detection, unlike
 *     the snippet this replaces).
 *   - `embedMode` is shipped as a commented-out optional line: placement
 *     choices (Widget placements tab) never change the site code, so the
 *     script can't know a target element id in advance. The Widget
 *     placements tab's "None" trigger renders a plain auto-launch container
 *     with no embedMode wiring, so on its own it opens the same full-screen
 *     modal as Banner/Button — the tab's copy says so and points back here.
 *   - `compatibleProducts.customerId` — required by the handbook (§3: "0" for
 *     guests, the real id when logged in) and missing from the snippet this
 *     replaced. Added here unconditionally as "0"; a merchant identifying
 *     logged-in shoppers edits the placeholder themselves, same as
 *     merchantKey.
 *
 *   - `baseUrl` (Shopify/BigCommerce/Magento, trailing slash) and `logoUrl`
 *     (every platform) — listed as required platform-config fields by the
 *     handbook and read by the runtime (`core/platform.ts` *BaseUrl,
 *     `CMPT_CONF_CLIENT_LOGO_URL`). Left out until ADO #15321 confirmed them.
 *   - `window.compatioBuilderAddtoCart(items, analyticsData)` — the builder's
 *     Add to Cart button calls it on the merchant's page
 *     (`CMPT_CONF_COMPATIO_ADD_TO_CART`, case-sensitive). Without it nothing
 *     reaches the cart and the shopper gets a 25 s timeout, so the Script
 *     ships one (ADO #15321): a working `/cart/add.js` call on Shopify, where
 *     `productId` is already the variant id, a template everywhere else.
 *   - Shopify's token key is `shopifyStorefrontAccessToken`
 *     (`CMPT_CONF_CLIENT_SHOPIFY_ACCESS_TOKEN`). The `shopifyAccessToken` this
 *     file used to emit is never read, so requests went out unauthenticated.
 *   - `dcConfig[].details.description` / `.imageUrl` — required per the
 *     handbook (§3) and both render in the banner/PDP mockups, not metadata —
 *     but nothing today captures either value per widget; there is no
 *     Create/Edit-widget field for them. Emitted here as literal placeholders
 *     ("YOUR_WIDGET_DESCRIPTION" / "YOUR_IMAGE_URL") the merchant edits by
 *     hand, the same convention as merchantKey/apiEndpointUrl/productSku
 *     above — closes the "missing from the generated code entirely" gap
 *     without inventing the data-model UI to auto-fill them.
 *
 * Copyright (c) 2026 Compatio LLC, Springfield, MO - www.compatio.ai
 * Protected by the Trade Secrets Act.
 */

export type TEmbedParam = { paramKey: string };

export type TEmbedEnv = { key: string; label: string; host: string; scriptUrl: string };

export type TPlatform = 'custom' | 'shopify' | 'bigcommerce' | 'magento';

export type TIdentifierMode = 'modelId' | 'widgetId';

export type TTrigger = 'banner' | 'button' | 'none';

export type TStartingState = 'blank' | 'product';

/** One widget as the site-code / placement / param generators need it. */
export type TEmbedWidget = {
    /** Widget id, dashes already stripped — the public widgetId. */
    id: string;
    title: string;
    modelId: string;
    modelName: string;
};

/**
 * Script hosts per environment. Paths follow isr-runtime-generic's deploy
 * script: MAJOR_VERSION is "v3" and isr-embed.js sits at <host>/v3/.
 * Production is listed first and is the default selection.
 */
export const EMBED_ENVIRONMENTS: TEmbedEnv[] = [
    { key: 'prod', label: 'Production', host: 'smartbuilder.compatio.ai', scriptUrl: 'https://smartbuilder.compatio.ai/v3/isr-embed.js' },
    { key: 'staging', label: 'Staging', host: 'staging-sb3-runtime.compatio.net', scriptUrl: 'https://staging-sb3-runtime.compatio.net/v3/isr-embed.js' },
];

export const PLATFORMS: { key: TPlatform; label: string }[] = [
    { key: 'custom', label: 'Custom' },
    { key: 'shopify', label: 'Shopify' },
    { key: 'bigcommerce', label: 'BigCommerce' },
    { key: 'magento', label: 'Magento 2' },
];

/**
 * Identifier defaults to Widget ID (ADO #14214 US-2 AC 7) — with a real
 * widget key always on hand here (site code is generated per-channel, not
 * per-widget), Widget ID is the natural default; Model ID stays available
 * for merchants still on that contract.
 */
export const IDENTIFIER_MODES: { key: TIdentifierMode; label: string; description: string }[] = [
    { key: 'widgetId', label: 'Widget ID', description: 'Default. Stays valid when a widget is pointed at a different model.' },
    { key: 'modelId', label: 'Model ID', description: 'For merchants still on the older contract. Breaks if the model is replaced.' },
];

export const TRIGGERS: { key: TTrigger; label: string; description: string }[] = [
    { key: 'banner', label: 'Banner', description: 'Compatio renders the banner — image, heading and button all come from the model. No extra code needed.' },
    { key: 'button', label: 'Button', description: 'A standalone button you style yourself. Compatio only listens for the click. Needs its own snippet on the page.' },
    { key: 'none', label: 'None', description: 'No trigger. The builder renders as soon as the page loads.' },
];

export const STARTING_STATES: { key: TStartingState; label: string; description: string }[] = [
    { key: 'blank', label: 'Blank builder', description: 'Opens with nothing chosen. Picking a product line is the shopper’s first step.' },
    { key: 'product', label: 'Preselected SKU', description: 'Opens with the page’s product already in the build, supplied by your page at runtime. If the shopper already has a build in progress, that resumes instead.' },
];

/** "Popup" never appears in merchant-facing copy (ADO #14215 US-3 AC 5) — the mode is "Full screen". */
export const DISPLAY_LABEL: Record<'inpage' | 'full', string> = { inpage: 'In the page', full: 'Full screen' };

export const DISPLAY_DESCRIPTION: Record<'inpage' | 'full', string> = {
    // The None trigger's own snippet is a plain auto-launch container — reaching
    // "in the page" needs embedMode configured too (see the None foot text below),
    // or this container opens the same full-screen modal as Banner/Button.
    inpage: 'Renders inside the page, with your header and navigation still visible — once embedMode is also uncommented and configured in the Site code tab’s Script. Without that, this still opens full screen. This is how you build a dedicated builder page.',
    full: 'Covers the window while the shopper designs, then closes back to the page they came from.',
};

/** Banner and Button both open as a modal; only a trigger-less (None) container renders in place. */
export const displayFor = (trigger: TTrigger): 'inpage' | 'full' => (trigger === 'none' ? 'inpage' : 'full');

/**
 * Per-platform product-SKU template expressions (Jabir's sample, settled
 * 14 Sept for both US-2 and US-3). The SKU changes per page, so a static
 * placeholder is wrong everywhere a theme can derive it — Custom is the
 * exception: there is nothing to derive it from, so it keeps the placeholder.
 *
 * Shopify's Liquid `| json` filter self-quotes its output, so unlike the
 * other three platforms this expression carries NO surrounding quote
 * characters of its own — the attribute's quoting comes from whatever Liquid
 * renders. Custom/BigCommerce/Magento each embed their own literal `"` pair.
 */
export const SKU_EXPR: Record<TPlatform, string> = {
    custom: '"YOUR_PRODUCT_SKU"',
    shopify: '{% if product %}{{ product.selected_or_first_available_variant.sku | json }}{% else %}""{% endif %}',
    bigcommerce: '"{{#if product}}{{product.sku}}{{/if}}"',
    magento: '"<' + '?= $block->getProduct() ? $block->getProduct()->getSku() : \'\' ?' + '>"',
};

export const SKU_NOTE: Record<TPlatform, string> = {
    custom: 'Set data-product-sku to the SKU shown on the page, or leave it out to open with no base product.',
    shopify: 'The Liquid expression resolves the SKU per page — leave it as is.',
    bigcommerce: 'The Handlebars expression resolves per page — works only in theme files, not Script Manager.',
    magento: 'The PHP expression resolves the SKU per page — leave it as is.',
};

export const PLATFORM_DESCRIPTION: Record<TPlatform, string> = {
    custom: 'Any site or stack. No platform-specific values needed.',
    shopify: 'Adds your store URL and the Shopify Storefront access token the runtime needs — replace the placeholders with yours.',
    bigcommerce: 'Adds your store URL, the BigCommerce store hash and Storefront API token — replace the placeholders with yours.',
    magento: 'Adds your store URL and the Magento URL suffix the runtime needs to resolve product pages.',
};

export const PLATFORM_PASTE_INSTRUCTIONS: Record<TPlatform, string> = {
    custom: 'Paste in your base template, just above the closing </body> tag, on every page a widget can appear on.',
    shopify: 'Paste in Online Store → Themes → Edit code → theme.liquid, just above the closing </body> tag.',
    bigcommerce: 'Paste in Edit theme files → templates/layout/base.html, above </body> — not Script Manager, which cannot read the product on the page.',
    magento: 'Paste in Content → Design → Configuration → your theme → Footer Miscellaneous HTML.',
};

export const CATALOG_ENDPOINT_PLACEHOLDER: Record<TPlatform, string> = {
    custom: 'https://yourshop.com/api/products',
    shopify: 'https://your-store.myshopify.com/api/2024-07/graphql.json',
    bigcommerce: 'https://store-XXXX.mybigcommerce.com/graphql',
    magento: 'https://your-store.com/graphql',
};

/** paramKey -> {{UPPER_SNAKE}} placeholder, e.g. roomWidth -> {{ROOM_WIDTH}}. */
export const placeholderFor = (paramKey: string): string =>
    '{{' + paramKey.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase() + '}}';

const jsString = (value: string): string => JSON.stringify(value ?? '');

/** True for an absolute http(s) URL — the only catalog endpoint worth printing. */
export const isHttpUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (error) {
        return false;
    }
};

// Every widget's dcConfig identifier line. Only the FIRST entry carries the
// explanatory comment — repeating it on every entry is just noise.
const identifierLineFor = (identifierMode: TIdentifierMode, widget: TEmbedWidget, isFirst: boolean): string =>
    identifierMode === 'widgetId'
        ? `      widgetId: ${jsString(widget.id)},` + (isFirst ? '  // identifies this configurator (takes precedence over modelId)' : '')
        : `      modelId: ${jsString(widget.modelId)},`;

const identifierAttrFor = (identifierMode: TIdentifierMode, widget: TEmbedWidget): string =>
    identifierMode === 'widgetId'
        ? `data-widget-id="${widget.id}"`
        : `data-model-id="${widget.modelId}"`;

/** `baseUrl` placeholder per platform — Custom has none (the runtime never reads it there). */
export const BASE_URL_PLACEHOLDER: Partial<Record<TPlatform, string>> = {
    shopify: 'https://your-store.myshopify.com/',
    bigcommerce: 'https://your-store.com/',
    magento: 'https://your-store.com/',
};

type TConfigLine = { code: string; comment: string };

// Extra per-platform keys inside the compatioConfig[platForm] object.
const platformConfigLines = (platform: TPlatform): TConfigLine[] => {
    switch (platform) {
        case 'shopify':
            return [{ code: 'shopifyStorefrontAccessToken: "YOUR_SHOPIFY_STOREFRONT_ACCESS_TOKEN"', comment: 'REQUIRED on Shopify - your Storefront API access token' }];
        case 'bigcommerce':
            return [
                { code: 'storeHash: "YOUR_STORE_HASH"', comment: 'REQUIRED on BigCommerce' },
                { code: 'apiToken: "YOUR_API_TOKEN"', comment: 'REQUIRED on BigCommerce - your public Storefront API token only; this page is public' },
            ];
        case 'magento':
            return [{ code: 'urlSuffix: ".html"', comment: 'Magento 2 product-URL suffix' }];
        case 'custom':
        default:
            return [];
    }
};

/**
 * The `compatioBuilderAddtoCart` block. Shopify gets a call that works as is —
 * `productId` is already the variant id, and `Shopify.routes.root` keeps it
 * right on a localised storefront; every other platform gets a template to
 * point at its own cart API.
 */
const addToCartLines = (platform: TPlatform): string => {
    const request = platform === 'shopify'
        ? `    // items: [{ productId: "VARIANT_ID", quantity: 2 }] - on Shopify, productId is already the variant ID
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const res = await fetch(root + "cart/add.js", {  // Shopify's Ajax Cart API - works as is
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map(function (i) {
            return { id: i.productId, quantity: i.quantity };
          })
        })
      });`
        : `    // items: [{ productId: "SKU", quantity: 2 }]
    try {
      const res = await fetch("YOUR_CART_API_ENDPOINT", {  // REQUIRED - your add-to-cart endpoint
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // CHANGE - map to the shape your cart API expects
          lines: items.map(function (i) {
            return { sku: i.productId, qty: i.quantity };
          })
        })
      });`;

    return `  // REQUIRED - connects the builder's "Add to Cart" button to YOUR cart.
  // The name is case-sensitive. It must return a Promise that resolves to an object:
  //   { success: true }                    -> items added, builder closes
  //   { success: false, held: true }       -> shopper closed your popup: nothing shown, builder stays open
  //   { success: false, message: "..." }   -> red message shown to the shopper, builder stays open
  //                                           (no message: "Product could not be added")
  // Returning nothing breaks the button; never resolving shows a timeout after 25 seconds.
  // Check it: in the browser console, run
  //   await window.compatioBuilderAddtoCart([{ productId: "YOUR-SKU", quantity: 1 }], {})
  // It must print { success: true }, and the item must be in your cart.
  window.compatioBuilderAddtoCart = async function (items, analyticsData) {
${request}
      if (!res.ok) {
        return { success: false, message: "We could not add these items to your cart. Please try again." };
      }
      return { success: true };  // ALWAYS return an object, even on success
    } catch (err) {
      return { success: false, message: "Connection problem. Please try again." };
    }
  };`;
};

export type TBuildSiteScriptOptions = {
    merchantKey: string;
    platform: TPlatform;
    env: TEmbedEnv;
    identifierMode: TIdentifierMode;
    /** Trimmed by the caller; blank renders the placeholder. */
    catalogEndpoint: string;
    widgets: TEmbedWidget[];
};

/**
 * The channel-wide Site code Script block — one per channel, listing every
 * widget. Re-copy whenever widgets change (ADO #14214 US-2 AC 3); placement
 * choices (Widget placements tab) never affect this block.
 */
export const buildSiteScript = (opts: TBuildSiteScriptOptions): string => {
    const { merchantKey, platform, env, identifierMode, catalogEndpoint, widgets } = opts;

    const entries = widgets
        .map((widget, index) => {
            const idLine = identifierLineFor(identifierMode, widget, index === 0);
            return `      {\n${idLine}\n        default: "1",\n        details: {\n          name: ${jsString(widget.modelName)},\n          title: ${jsString(widget.title)},\n          description: "YOUR_WIDGET_DESCRIPTION",  // REQUIRED - shown next to the image on the banner\n          imageUrl: "YOUR_IMAGE_URL",               // REQUIRED - card thumbnail; falls back to a placeholder if it fails to load\n          buttonText: "Start Configuration"\n        }\n      }`;
        })
        .join(',\n');

    // REQUIRED on Magento/Shopify/BigCommerce, OPTIONAL only on Custom (handbook § 3) —
    // the comment must say which, or a merchant on a required platform reads "OPTIONAL"
    // and leaves the placeholder in place.
    const endpointRequirement = platform === 'custom' ? 'OPTIONAL' : 'REQUIRED';
    // Only a real URL is printed: anything else (a saved "test", say) would ship
    // as the merchant's endpoint, so it falls back to the placeholder (ADO #15321).
    const endpoint = catalogEndpoint.trim();
    const baseUrl = BASE_URL_PLACEHOLDER[platform];

    const configLines: TConfigLine[] = [
        { code: `merchantKey: ${jsString(merchantKey.trim() || 'YOUR_MERCHANT_KEY')}`, comment: 'REQUIRED - your Compatio merchant key' },
        { code: `apiEndpointUrl: ${jsString(isHttpUrl(endpoint) ? endpoint : 'YOUR_PRODUCT_API_ENDPOINT')}`, comment: `${endpointRequirement} - your own catalog endpoint` },
        ...(baseUrl ? [{ code: `baseUrl: ${jsString(baseUrl)}`, comment: 'REQUIRED - your store URL, with the trailing slash' }] : []),
        { code: 'logoUrl: "YOUR_LOGO_URL"', comment: 'REQUIRED - your logo, shown in the builder header' },
        ...platformConfigLines(platform),
    ];
    const configBody = configLines
        .map((line, index) => `    ${line.code}${index < configLines.length - 1 ? ',' : ''}  // ${line.comment}`)
        .join('\n');

    return `<!-- Compatio Guide — site setup — ${env.label} -->
<script>
  window.compatioConfig = window.compatioConfig || {};
  window.compatioConfig.${platform} = {
${configBody}
  };
  window.compatioConfig.platForm = ${jsString(platform)};
  window.compatioConfig.compatibleProducts = {
    customerId: "0",  // REQUIRED - "0" for guests, or your logged-in customer id
    // OPTIONAL - open the builder around the page’s product. Drop the
    // line entirely to open a builder with no base product.
    productSku: ${SKU_EXPR[platform]},
    dcConfig: [
${entries}
    ]
  };
  window.compatioConfig.compatibleProducts.newUI = true;  // renders the SB 3.x builder
  // OPTIONAL — only if a builder renders in the page (None trigger). One element per site.
  // window.compatioConfig.embedMode = { embed: true, targetElement: "#compatio-builder", autoLaunch: true };

${addToCartLines(platform)}
</script>

<!-- Each environment’s script carries its own engine API URL, so this is
     the only line that differs between environments. -->
<script src="${env.scriptUrl}"></script>`;
};

/**
 * The Site code HTML block — the shared save/share container and the shared
 * banner container. Ships for every merchant, one of each per page, copy once
 * ever (ADO #14214 US-2 AC 3/7).
 */
export const buildSiteHtml = (): string =>
    '<div class="compatio-builder-container"></div> <!-- enables save/share + #configure hash-resume -->\n' +
    '<div id="sb3-container" data-resume-hash="true"></div>   <!-- banner widgets render here -->';

export type TBuildPlacementSnippetOptions = {
    widget: TEmbedWidget;
    trigger: TTrigger;
    startingState: TStartingState;
    identifierMode: TIdentifierMode;
    platform: TPlatform;
};

/**
 * Widget placements tab: widget + trigger + starting state in, one snippet
 * out. Nothing here is saved to the widget (ADO #14215 US-3) — this is a
 * generator, not stored settings.
 */
export const buildPlacementSnippet = (opts: TBuildPlacementSnippetOptions): string => {
    const { widget, trigger, startingState, identifierMode, platform } = opts;

    if (trigger === 'banner') {
        // The shared banner container — dcConfig-driven, no per-widget identifier needed.
        return '<div id="sb3-container" data-resume-hash="true"></div>';
    }

    const idAttr = identifierAttrFor(identifierMode, widget);
    const skuAttr = startingState === 'product' ? ` data-product-sku=${SKU_EXPR[platform]}` : '';

    return trigger === 'button'
        ? `<button class="compatio-builder-button" ${idAttr}${skuAttr}>Start Configuration</button>`
        : `<div id="compatio-builder" ${idAttr}${skuAttr} data-resume-hash="true"></div>`;
};

/**
 * Input parameters tab: the per-widget VDA pre-fill block, read at
 * model-spec fetch time from window.compatioConfig.inputParameter. Empty
 * params list means the widget's model declares none — callers should filter
 * such widgets out of the tab entirely rather than call this for them.
 */
export const buildParamSnippet = (params: TEmbedParam[]): string => {
    if (!params.length) return '';
    return [
        '<script>',
        '  // OPTIONAL - input parameters declared on this model\'s Input Parameter',
        '  // tab. Replace each placeholder with a value from your page, or drop',
        '  // the whole block to open the model with its own defaults.',
        '  // Sent only when a base product SKU is set (productSku in the Site code',
        '  // script, or data-product-sku on the container); with no SKU they are',
        '  // ignored. A placeholder left in place is sent as a real value.',
        '  window.compatioConfig = window.compatioConfig || {};',
        '  window.compatioConfig.inputParameter = {',
        // Keys are quoted: a field name like `Flow Rate - Config` is a syntax
        // error bare, and stops the whole script (ADO #15321).
        ...params.map((p, i) => `    ${jsString(p.paramKey)}: ${jsString(placeholderFor(p.paramKey))}${i < params.length - 1 ? ',' : ''}`),
        '  };',
        '</script>',
    ].join('\n');
};
